// GTM dataLayer integration. Helpers are pure builders that return a payload.
// Dispatch happens inside `logEvent` (see src/analytics.ts).

import {rootStore} from 'stores/rootStore';
import {getLanguage} from 'i18n/LanguageProvider';
import {isIframeBased} from "utils";
import {deliveryFromEmail, letterFromEmail} from "utils/utm";
import type {HumanizerMode} from "config";
import type {ScanMeasurement} from 'utils/funnel';
import {getAssignedABVariant, type ABVariant} from 'utils/abTest';
import {EVENT_FLUSH_BUDGET_MS} from 'utils/eventDelivery';

type ScanType = 'simple_scan' | 'deep_scan' | 'plagiarism' | 'grammar';
/** What started a run, reported as the entrypoint of its scan_started events: the user pressing
 *  Run scan in the app, or the landing hand-off continuing — by itself, without a second press —
 *  the Scan the user already pressed on the landing (init.js, text and files alike). Keeping the
 *  two apart is the whole point: an automatic scan must not be counted as a click in the app. */
export type ScanEntrypoint = 'run_scan' | 'landing_autoscan';
/** The CTA context the landing appends to the app URL, because GTM cannot carry a dataLayer
 *  across the origin hop (its half is LANDING_GTM_BRIDGE_QUERY in the landing's
 *  lib/analytics/gtm.ts). No app code reads them — GTM does, off the URL, up to and including
 *  the app_open event — so init.js drops them once that event is out, exactly as it drops
 *  `from` and the utm_* params it has finished reporting. */
export const LANDING_GTM_BRIDGE_PARAMS = [
    'gtm_prior_cta',
    'gtm_prior_user_state',
    'gtm_prior_source_path',
    'gtm_prior_source_type',
];
export type LimitType = 'used_words' | 'deep_scans' | 'max_symbols' | 'grammar_checks' | 'plagiarism_checks' | 'humanizer_words';
export type TabName = 'scan' | 'history' | 'statistics' | 'api' | 'api_docs' | 'subscription' | 'ai_scan' | 'plagiarism' | 'grammar' | 'humanizer';

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[];
    }
}

export interface GtmPayload {
    event: `GTM_${string}`;
    params: Record<string, unknown>;
}

/** An event name that came in as a plain string (from @it-s-ai/auth), as a GTM_ one. */
export const gtmName = (event: string): GtmPayload['event'] =>
    event.startsWith('GTM_') ? event as GtmPayload['event'] : `GTM_${event}`;

function getCommonParams(page_type: string, entrypoint: string) {
    return {
        measurement_version: 2,
        // Attribution only: logging an event must never enrol someone in the test.
        humanizer_announcement_variant: getAssignedABVariant('humanizerAnnouncement'),
        page_path: window.location.pathname,
        page_language: getLanguage(),
        page_type,
        user_state: rootStore.user.isAnonymous ? 'anonymous' : 'authenticated',
        entrypoint,
        is_iframe: isIframeBased(),
        // Which letter this visitor arrived from, on every event rather than on
        // the arrival alone: the tags are stripped from the URL a moment after
        // the app opens, so a checkout an hour later could otherwise not be read
        // back to the email that started it. Absent when they came another way.
        email_letter_id: letterFromEmail(),
        // And which send of it — the same letter reaches thousands of people,
        // and this is what the backend resolves to the one reading.
        email_delivery_id: deliveryFromEmail(),
    };
}

// Caps the wait on eventCallback when GTM tags are slow or the container isn't
// loaded — otherwise a missing tag would hang an awaited push indefinitely.
const GTM_FLUSH_TIMEOUT = EVENT_FLUSH_BUDGET_MS;

export function pushGTM(event: string, params: Record<string, unknown>): Promise<void> {
    const data = {event, ...params};
    console.log("GTM", event, params);
    if (isIframeBased()) {
        // postMessage is synchronous from our side; parent's processing is out of our control.
        window.parent.postMessage({type: 'gtm-event', payload: data}, '*');
        return Promise.resolve();
    }
    window.dataLayer = window.dataLayer || [];
    return new Promise(resolve => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const finish = () => {
            if (timeoutId === null) return;
            clearTimeout(timeoutId);
            timeoutId = null;
            resolve();
        };
        timeoutId = setTimeout(finish, GTM_FLUSH_TIMEOUT);
        window.dataLayer.push({...data, eventCallback: finish, eventTimeout: GTM_FLUSH_TIMEOUT});
    });
}


export function gtmAppOpen(params: {
    page_location: string;
    page_referrer: string;
    page_title: string;
    page_type: string;
    entrypoint: string;
}): GtmPayload {
    return {
        event: 'GTM_app_open',
        params: {...getCommonParams(params.page_type, params.entrypoint), ...params},
    };
}

/**
 * A visit that arrived with UTM tags — one event with the tags as parameters,
 * rather than a name built out of them. `utm__email__welcome:2-try-scan` was a
 * distinct event per letter: unregisterable in the events sheet, unusable as a
 * GTM trigger, and impossible to count without listing every letter first.
 */
export function gtmUtmArrival(params: {
    utm_source: string;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_content?: string | null;
    utm_term?: string | null;
    page_type: string;
    entrypoint: string;
}): GtmPayload {
    const {page_type, entrypoint, ...tags} = params;
    return {
        event: 'GTM_utm_arrival',
        params: {...getCommonParams(page_type, entrypoint), ...tags},
    };
}

export function gtmAppScanStarted(params: {
    scan_type: ScanType;
    page_type: string;
    entrypoint: ScanEntrypoint;
} & ScanMeasurement): GtmPayload {
    const {page_type, entrypoint, ...rest} = params;
    return {
        event: 'GTM_app_scan_started',
        params: {...getCommonParams(page_type, entrypoint), ...rest},
    };
}

export function gtmAppScanSuccess(params: {
    scan_type: ScanType;
    scan_result: string;
    page_type: string;
    entrypoint: string;
} & ScanMeasurement): GtmPayload {
    const {page_type, entrypoint, ...rest} = params;
    return {
        event: 'GTM_app_scan_success',
        params: {...getCommonParams(page_type, entrypoint), ...rest},
    };
}

export function gtmAppScanError(params: {
    scan_type: string;
    error_code: string;
    error_message: string;
    error_stage: string;
    page_type: string;
    entrypoint: string;
} & ScanMeasurement): GtmPayload {
    const {page_type, entrypoint, ...rest} = params;
    return {
        event: 'GTM_app_scan_error',
        params: {...getCommonParams(page_type, entrypoint), ...rest},
    };
}

export function gtmFreeLimitReached(params: {
    context?: Record<string, unknown>;
    paywall_trigger?: string;
    limit_type: LimitType;
    limit_value?: string;
    page_type: string;
    entrypoint: string;
    popup_type?: string;
    limit_source?: string;
    cta_variant?: string;
    billing_default_variant?: string;
    // Which purchase surface the paywall shows: inline card checkout,
    // one-click upgrade (already paid), or enterprise contact-sales.
    checkout_variant?: string;
}): GtmPayload {
    const {page_type, entrypoint, context, ...rest} = params;
    return {
        event: 'GTM_free_limit_reached',
        params: {...getCommonParams(page_type, entrypoint), ...context, ...rest},
    };
}

/** Any other app event: the common params plus its own. */
export function gtmEvent(
    event: `GTM_${string}`,
    {page_type, entrypoint, ...params}: {page_type: string; entrypoint: string; [key: string]: unknown},
): GtmPayload {
    return {event, params: {...getCommonParams(page_type, entrypoint), ...params}};
}

export function gtmScanMeasurement(event: GtmPayload['event'], params: Record<string, unknown>): GtmPayload {
    return {event, params: {...getCommonParams('app_scan', 'scan'), ...params}};
}

export function gtmLimitsReachedPopupAnnualSelect(params: {
    limit_type: string;
    plan_key?: string;
    plan_name?: string;
    page_type: string;
    entrypoint?: string;
}): GtmPayload {
    return {
        event: 'GTM_limits_reached_popup_annual_select',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'limits_reached_popup'),
            popup_type: 'limits_reached',
            limit_type: params.limit_type,
            billing_period: 'yearly',
            plan_key: params.plan_key ?? 'premium',
            plan_name: params.plan_name ?? 'premium',
        },
    };
}

export function gtmLimitsReachedPopupMonthlySelect(params: {
    limit_type: string;
    plan_key?: string;
    plan_name?: string;
    page_type: string;
    entrypoint?: string;
}): GtmPayload {
    return {
        event: 'GTM_limits_reached_popup_monthly_select',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'limits_reached_popup'),
            popup_type: 'limits_reached',
            limit_type: params.limit_type,
            billing_period: 'monthly',
            plan_key: params.plan_key ?? 'premium',
            plan_name: params.plan_name ?? 'premium',
        },
    };
}

export function gtmLimitsReachedPopupUpgradeClick(params: {
    limit_type: string;
    billing_period: string;
    plan_key?: string;
    plan_name?: string;
    page_type: string;
    entrypoint?: string;
    // new_sub = free/anon inline checkout · upgrade_existing = one-click paid upgrade.
    flow?: string;
    payment_method?: string;
}): GtmPayload {
    return {
        event: 'GTM_limits_reached_popup_upgrade_click',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'limits_reached_popup'),
            popup_type: 'limits_reached',
            limit_type: params.limit_type,
            billing_period: params.billing_period,
            plan_key: params.plan_key ?? 'premium',
            plan_name: params.plan_name ?? 'premium',
            flow: params.flow,
            payment_method: params.payment_method,
        },
    };
}

export function gtmLimitsReachedPopupClose(params: {
    limit_type: string;
    billing_period?: string;
    page_type: string;
    entrypoint?: string;
}): GtmPayload {
    return {
        event: 'GTM_limits_reached_popup_close',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'limits_reached_popup'),
            popup_type: 'limits_reached',
            limit_type: params.limit_type,
            billing_period: params.billing_period,
        },
    };
}

export function gtmLimitsReachedPopupContinueTruncated(params: {
    limit_type: string;
    page_type: string;
    text_len: number;
    over_chars: number;
    entrypoint?: string;
}): GtmPayload {
    return {
        event: 'GTM_limits_reached_popup_continue_truncated',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'limits_reached_popup'),
            popup_type: 'limits_reached',
            limit_type: params.limit_type,
            text_len: params.text_len,
            over_chars: params.over_chars,
        },
    };
}

export function gtmOnboardingPopupShown(params: {
    page_type: string;
    popup_type?: string;
    onboarding_step?: string;
    entrypoint?: string;
}): GtmPayload {
    return {
        event: 'GTM_onboarding_popup_shown',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'signup_onboarding'),
            popup_type: params.popup_type ?? 'post_signup_profile_completion',
            onboarding_step: params.onboarding_step ?? 'profile_completion',
        },
    };
}

export function gtmOnboardingPopupCtaClick(params: {
    page_type: string;
    popup_type?: string;
    onboarding_step?: string;
    cta_label?: string;
    entrypoint?: string;
}): GtmPayload {
    return {
        event: 'GTM_onboarding_popup_cta_click',
        params: {
            ...getCommonParams(params.page_type, params.entrypoint ?? 'signup_onboarding'),
            popup_type: params.popup_type ?? 'post_signup_profile_completion',
            onboarding_step: params.onboarding_step ?? 'profile_completion',
            cta_label: params.cta_label ?? 'get_started',
        },
    };
}

export function gtmSubscriptionsView(params: {
    page_type: string;
    entrypoint: string;
    billing_default_variant?: string;
}): GtmPayload {
    const {page_type, entrypoint, ...rest} = params;
    return {
        event: 'GTM_subscriptions_view',
        params: {...getCommonParams(page_type, entrypoint), ...rest},
    };
}

export function gtmCheckoutStarted(params: {
    plan_name: string;
    billing_period: string;
    payment_provider: string;
    value: number;
    currency: string;
    trial: boolean;
    page_type: string;
    entrypoint: string;
    billing_default_variant?: string;
    checkout_id?: string;
    trial_variant?: string;
    offer_kind?: string;
    checkout_session_id?: string;
}): GtmPayload {
    const {page_type, entrypoint, ...rest} = params;
    return {
        event: 'GTM_checkout_started',
        params: {...getCommonParams(page_type, entrypoint), ...rest},
    };
}

export function gtmStripeRedirectStarted(params: {
    checkout_session_id?: string;
    plan_name: string;
    plan_key: string;
    billing_period: string;
    value: number;
    currency: string;
    trial: boolean;
    source: string;
    page_type: string;
    entrypoint: string;
    billing_default_variant?: string;
    checkout_id?: string;
    trial_variant?: string;
    offer_kind?: string;
}): GtmPayload {
    const {page_type, entrypoint, checkout_session_id, ...rest} = params;
    const stripe_mode = checkout_session_id?.startsWith('cs_live_')
        ? 'live'
        : checkout_session_id
            ? 'test'
            : undefined;
    return {
        event: 'GTM_stripe_redirect_started',
        params: {
            ...getCommonParams(page_type, entrypoint),
            checkout_session_id,
            stripe_mode,
            page_location: window.location.href,
            page_title: document.title,
            ...rest,
        },
    };
}

export function gtmPaymentCompleted(params: {
    plan_name: string;
    billing_period: string;
    payment_provider: string;
    value: number;
    currency: string;
    trial: boolean;
    page_type: string;
    entrypoint: string;
    billing_default_variant?: string;
    checkout_id?: string;
    trial_variant?: string;
    offer_kind?: string;
    checkout_session_id?: string;
}): GtmPayload {
    const {page_type, entrypoint, ...rest} = params;
    return {
        event: 'GTM_payment_completed',
        params: {...getCommonParams(page_type, entrypoint), ...rest},
    };
}

/** Where a certificate was asked for: the scan's own button (text run or the batch's
 *  selected file), a report opened by its link, a batch file's row, the batch's
 *  "Get all certificates", or a history row. */
export type CertificateSurface = 'scan_panel' | 'report_page' | 'batch_file' | 'batch_all' | 'history';
const CERTIFICATE_PAGE_TYPE: Record<CertificateSurface, string> = {
    scan_panel: 'app_scan',
    report_page: 'report',
    batch_file: 'app_scan',
    batch_all: 'app_scan',
    history: 'app_dashboard',
};
interface CertificateActionParams {
    /** The PDF reached the browser's downloads; `files` counts them for "Get all certificates". */
    download: {scan_type?: ScanType; files?: number};
    /** The sign-in wall stood in the way of an anon. */
    wall: {wall: 'login'};
    download_error: {error_code: string; files?: number};
}

/** A certificate step, as `GTM_certificate_<action>`. entrypoint is always
 *  'download_certificate': the GTM trigger matches on it. */
export function gtmCertificate<A extends keyof CertificateActionParams>(
    action: A,
    surface: CertificateSurface,
    params: CertificateActionParams[A],
): GtmPayload {
    return {
        event: `GTM_certificate_${action}`,
        params: {...getCommonParams(CERTIFICATE_PAGE_TYPE[surface], 'download_certificate'), surface, ...params},
    };
}

export function gtmAppTabView(params: {
    tab_name: TabName;
    page_type: string;
    entrypoint: string;
}): GtmPayload {
    return {
        event: 'GTM_app_tab_view',
        params: {...getCommonParams(params.page_type, params.entrypoint), tab_name: params.tab_name},
    };
}

export function gtmApiKeyCreated(params: {
    page_type: string;
    entrypoint: string;
}): GtmPayload {
    return {
        event: 'GTM_api_key_created',
        params: getCommonParams(params.page_type, params.entrypoint),
    };
}

export function gtmApiKeyDeleted(params: {
    page_type: string;
    entrypoint: string;
}): GtmPayload {
    return {
        event: 'GTM_api_key_deleted',
        params: getCommonParams(params.page_type, params.entrypoint),
    };
}

type HumanizerSide = 'input' | 'output';
type HumanizerRun = {mode: HumanizerMode; words: number};
interface HumanizerActionParams {
    copy: {side: HumanizerSide};
    history_copy: {side: HumanizerSide};
    history_restore: {failed: boolean};
    undo: {};
    redo: {};
    run: HumanizerRun & {rerun: boolean};
    run_success: HumanizerRun & {output_words: number};
    run_error: HumanizerRun & {error_code: string};
    mode_change: {mode: HumanizerMode};
    paste: {};
    file_upload: {failed: boolean};
    humanize_first_words: {limit: number};
    word_counter_click: {};
    /** Milliseconds from the start of a request; null when no preview arrived. */
    result_timing: {
        task_id: number;
        first_preview_received_ms: number | null;
        first_preview_visible_ms: number | null;
        result_received_ms: number;
        result_displayed_ms: number;
        reveal_tail_ms: number;
    };
    rescan: {side: HumanizerSide};
    score_popover_open: {side: HumanizerSide};
    view_full_analysis: {side: HumanizerSide};
}
/** Where a Humanizer control was used: the scan page (panes and the history under them),
 *  or the phone's History page, which lists the same runs. */
export type HumanizerSurface = 'scan' | 'history_page';

/** A control used in the Humanizer, as `GTM_humanizer_<action>`. The shared prefix lets
 *  one `^GTM_humanizer_` trigger in GTM forward the whole family to GA4. */
export function gtmHumanizer<A extends keyof HumanizerActionParams>(
    action: A,
    params: HumanizerActionParams[A],
    surface: HumanizerSurface = 'scan',
): GtmPayload {
    const common = surface === 'scan'
        ? getCommonParams('app_scan', 'humanizer')
        : getCommonParams('app_dashboard', 'history_page');
    return {event: `GTM_humanizer_${action}`, params: {...common, ...params}};
}

// Generic popup helper: keeps the per-event GTM convention but reduces boilerplate.
function popupEvent(event: GtmPayload['event'], popup_type: string, entrypoint: string, page_type: string, extra: Record<string, unknown> = {}): GtmPayload {
    return {
        event,
        params: {...getCommonParams(page_type, entrypoint), popup_type, ...extra},
    };
}

// ---------- Free premium popup ----------
export function gtmFreePremiumPopupShown(params: {page_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_free_premium_popup_shown', 'free_premium_popup', 'free_premium_popup', params.page_type ?? 'app_scan');
}
export function gtmFreePremiumPopupCtaClick(params: {cta_label: 'accept' | 'decline'; page_type?: string}): GtmPayload {
    return popupEvent('GTM_free_premium_popup_cta_click', 'free_premium_popup', 'free_premium_popup', params.page_type ?? 'app_scan', {cta_label: params.cta_label});
}
export function gtmFreePremiumPopupClose(params: {page_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_free_premium_popup_close', 'free_premium_popup', 'free_premium_popup', params.page_type ?? 'app_scan');
}

// ---------- Free premium feedback popup ----------
export function gtmFreePremiumFeedbackShown(params: {page_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_free_premium_feedback_shown', 'free_premium_feedback', 'free_premium_feedback_popup', params.page_type ?? 'app_scan');
}
export function gtmFreePremiumFeedbackSubmit(params: {rating: number; has_text: boolean; page_type?: string}): GtmPayload {
    return popupEvent('GTM_free_premium_feedback_submit', 'free_premium_feedback', 'free_premium_feedback_popup', params.page_type ?? 'app_scan', {rating: params.rating, has_text: params.has_text});
}
export function gtmFreePremiumFeedbackClose(params: {page_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_free_premium_feedback_close', 'free_premium_feedback', 'free_premium_feedback_popup', params.page_type ?? 'app_scan');
}
export function gtmFreePremiumFeedbackDontShowClick(params: {page_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_free_premium_feedback_dont_show_click', 'free_premium_feedback', 'free_premium_feedback_popup', params.page_type ?? 'app_scan');
}

// ---------- Subscription educational popup ----------
export function gtmSubscriptionEducationalShown(): GtmPayload {
    return popupEvent('GTM_subscription_educational_shown', 'subscription_educational', 'subscription_educational_popup', 'subscriptions');
}
export function gtmSubscriptionEducationalClose(): GtmPayload {
    return popupEvent('GTM_subscription_educational_close', 'subscription_educational', 'subscription_educational_popup', 'subscriptions');
}

// ---------- Subscribe request sent popup ----------
export function gtmSubscribeRequestSentShown(): GtmPayload {
    return popupEvent('GTM_subscribe_request_sent_shown', 'subscribe_request_sent', 'subscribe_request_sent_popup', 'subscriptions');
}
export function gtmSubscribeRequestSentClose(): GtmPayload {
    return popupEvent('GTM_subscribe_request_sent_close', 'subscribe_request_sent', 'subscribe_request_sent_popup', 'subscriptions');
}

// ---------- Subscription confirm popup ----------
export function gtmSubscriptionConfirmShown(params: {confirm_type?: string; plan_name?: string} = {}): GtmPayload {
    return popupEvent('GTM_subscription_confirm_shown', 'subscription_confirm', 'subscription_confirm_popup', 'subscriptions', {confirm_type: params.confirm_type, plan_name: params.plan_name});
}
export function gtmSubscriptionConfirmCtaClick(params: {confirm_type?: string; plan_name?: string} = {}): GtmPayload {
    return popupEvent('GTM_subscription_confirm_cta_click', 'subscription_confirm', 'subscription_confirm_popup', 'subscriptions', {confirm_type: params.confirm_type, plan_name: params.plan_name});
}
export function gtmSubscriptionConfirmClose(params: {confirm_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_subscription_confirm_close', 'subscription_confirm', 'subscription_confirm_popup', 'subscriptions', {confirm_type: params.confirm_type});
}

// ---------- Subscription change preview popup ----------
export function gtmSubscriptionChangePreviewShown(params: {plan_name?: string; billing_period?: string} = {}): GtmPayload {
    return popupEvent('GTM_subscription_change_preview_shown', 'subscription_change_preview', 'subscription_change_preview_popup', 'subscriptions', {plan_name: params.plan_name, billing_period: params.billing_period});
}
export function gtmSubscriptionChangePreviewConfirm(params: {plan_name?: string; billing_period?: string} = {}): GtmPayload {
    return popupEvent('GTM_subscription_change_preview_confirm', 'subscription_change_preview', 'subscription_change_preview_popup', 'subscriptions', {plan_name: params.plan_name, billing_period: params.billing_period});
}
export function gtmSubscriptionChangePreviewClose(params: {plan_name?: string; billing_period?: string} = {}): GtmPayload {
    return popupEvent('GTM_subscription_change_preview_close', 'subscription_change_preview', 'subscription_change_preview_popup', 'subscriptions', {plan_name: params.plan_name, billing_period: params.billing_period});
}

// ---------- Cancel subscription popup ----------
export function gtmCancelSubscriptionShown(): GtmPayload {
    return popupEvent('GTM_cancel_subscription_shown', 'cancel_subscription', 'cancel_subscription_popup', 'subscriptions');
}
export function gtmCancelSubscriptionConfirm(): GtmPayload {
    return popupEvent('GTM_cancel_subscription_confirm', 'cancel_subscription', 'cancel_subscription_popup', 'subscriptions');
}
export function gtmCancelSubscriptionClose(): GtmPayload {
    return popupEvent('GTM_cancel_subscription_close', 'cancel_subscription', 'cancel_subscription_popup', 'subscriptions');
}

// ---------- Premium activated popup ----------
export function gtmPremiumActivatedShown(): GtmPayload {
    return popupEvent('GTM_premium_activated_shown', 'premium_activated', 'premium_activated_popup', 'subscriptions');
}
export function gtmPremiumActivatedCtaClick(): GtmPayload {
    return popupEvent('GTM_premium_activated_cta_click', 'premium_activated', 'premium_activated_popup', 'subscriptions', {cta_label: 'explore'});
}
export function gtmPremiumActivatedClose(): GtmPayload {
    return popupEvent('GTM_premium_activated_close', 'premium_activated', 'premium_activated_popup', 'subscriptions');
}

// ---------- Release announcement popup (announcements/) ----------
/** Both arms reach this event at the same eligible popup opportunity, including hide. */
export function gtmAnnouncementExperimentExposure(announcement_id: string, variant: ABVariant<'humanizerAnnouncement'>): GtmPayload {
    return {event: 'GTM_announcement_experiment_exposure', params: {
        ...getCommonParams('app_scan', 'announcement_popup'), announcement_id,
        experiment_id: 'ab_humanizer_announcement_v1', humanizer_announcement_variant: variant,
    }};
}

export function gtmAnnouncementShown(announcement_id: string): GtmPayload {
    return popupEvent('GTM_announcement_shown', 'announcement', 'announcement_popup', 'app_scan', {announcement_id});
}
export function gtmAnnouncementCtaClick(announcement_id: string): GtmPayload {
    return popupEvent('GTM_announcement_cta_click', 'announcement', 'announcement_popup', 'app_scan', {announcement_id});
}
export function gtmAnnouncementClose(announcement_id: string): GtmPayload {
    return popupEvent('GTM_announcement_close', 'announcement', 'announcement_popup', 'app_scan', {announcement_id});
}

// ---------- Free premium questionnaire popup ----------
export function gtmFreePremiumQuestionnaireShown(): GtmPayload {
    return popupEvent('GTM_free_premium_questionnaire_shown', 'free_premium_questionnaire', 'free_premium_questionnaire_popup', 'app_scan');
}
export function gtmFreePremiumQuestionnaireSubmit(): GtmPayload {
    return popupEvent('GTM_free_premium_questionnaire_submit', 'free_premium_questionnaire', 'free_premium_questionnaire_popup', 'app_scan');
}
export function gtmFreePremiumQuestionnaireClose(): GtmPayload {
    return popupEvent('GTM_free_premium_questionnaire_close', 'free_premium_questionnaire', 'free_premium_questionnaire_popup', 'app_scan');
}

// ---------- Questionnaire thank-you popup ----------
export function gtmQuestionnaireThankyouShown(): GtmPayload {
    return popupEvent('GTM_questionnaire_thankyou_shown', 'questionnaire_thankyou', 'questionnaire_thankyou_popup', 'app_scan');
}
export function gtmQuestionnaireThankyouClose(): GtmPayload {
    return popupEvent('GTM_questionnaire_thankyou_close', 'questionnaire_thankyou', 'questionnaire_thankyou_popup', 'app_scan');
}

// ---------- Plagiarism result modal ----------
export function gtmPlagiarismModalShown(params: {score?: string} = {}): GtmPayload {
    return popupEvent('GTM_plagiarism_modal_shown', 'plagiarism_modal', 'plagiarism_modal_popup', 'app_scan', {score: params.score});
}
export function gtmPlagiarismModalClose(): GtmPayload {
    return popupEvent('GTM_plagiarism_modal_close', 'plagiarism_modal', 'plagiarism_modal_popup', 'app_scan');
}

// ---------- Login prompt popup ----------
export function gtmLoginPromptShown(): GtmPayload {
    return popupEvent('GTM_login_prompt_shown', 'login_prompt', 'login_prompt_popup', 'app_scan');
}

// ---------- Policy popup ----------
export function gtmPolicyPopupShown(params: {policy_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_policy_popup_shown', 'policy', 'policy_popup', 'app_scan', {policy_type: params.policy_type});
}
export function gtmPolicyPopupClose(params: {policy_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_policy_popup_close', 'policy', 'policy_popup', 'app_scan', {policy_type: params.policy_type});
}

// ---------- Permission guard popup ----------
export function gtmPermissionGuardUpgradeClick(params: {guard_type?: string; billing_period: string}): GtmPayload {
    return popupEvent('GTM_permission_guard_upgrade_click', 'permission_guard', 'permission_guard_popup', 'app_scan', params);
}
/** A step of the limits wall with no dedicated builder (continue, continue_basic). */
export function gtmLimitsReachedPopupStep(
    action: 'continue' | 'continue_basic',
    params: Record<string, unknown>,
    page_type: string = 'app_scan',
): GtmPayload {
    return popupEvent(`GTM_limits_reached_popup_${action}`, 'limits_reached', 'limits_reached_popup', page_type, params);
}
export function gtmPermissionGuardShown(params: {guard_type?: string; billing_default_variant?: string} = {}): GtmPayload {
    return popupEvent('GTM_permission_guard_shown', 'permission_guard', 'permission_guard_popup', 'app_scan', {guard_type: params.guard_type, billing_default_variant: params.billing_default_variant});
}
export function gtmPermissionGuardClose(params: {guard_type?: string} = {}): GtmPayload {
    return popupEvent('GTM_permission_guard_close', 'permission_guard', 'permission_guard_popup', 'app_scan', {guard_type: params.guard_type});
}

// ---------- Book demo popup ----------
export function gtmBookDemoShown(): GtmPayload {
    return popupEvent('GTM_book_demo_shown', 'book_demo', 'book_demo_popup', 'subscriptions');
}
export function gtmBookDemoSubmit(): GtmPayload {
    return popupEvent('GTM_book_demo_submit', 'book_demo', 'book_demo_popup', 'subscriptions');
}
export function gtmBookDemoClose(): GtmPayload {
    return popupEvent('GTM_book_demo_close', 'book_demo', 'book_demo_popup', 'subscriptions');
}

// ---------- Exit batch scan popup ----------
export function gtmExitBatchScanShown(): GtmPayload {
    return popupEvent('GTM_exit_batch_scan_shown', 'exit_batch_scan', 'exit_batch_scan_popup', 'app_scan');
}
export function gtmExitBatchScanConfirm(): GtmPayload {
    return popupEvent('GTM_exit_batch_scan_confirm', 'exit_batch_scan', 'exit_batch_scan_popup', 'app_scan');
}
export function gtmExitBatchScanClose(): GtmPayload {
    return popupEvent('GTM_exit_batch_scan_close', 'exit_batch_scan', 'exit_batch_scan_popup', 'app_scan');
}

// ---------- "No plan covers this" popup (the sales-contact branch of IncreaseLimitsForm) ----------
// Renamed from GTM_enterprise_limits_*: the popup is no longer the Enterprise subscriber's
// dead end, it is shown to ANY buyer whose run is bigger than every plan we sell (a free user
// pasting 600k characters reaches it too), so the old name described the wrong thing.
export function gtmNoPlanCoversShown(): GtmPayload {
    return popupEvent('GTM_no_plan_covers_shown', 'no_plan_covers', 'no_plan_covers_popup', 'app_scan');
}
export function gtmNoPlanCoversClose(): GtmPayload {
    return popupEvent('GTM_no_plan_covers_close', 'no_plan_covers', 'no_plan_covers_popup', 'app_scan');
}
export function gtmNoPlanCoversContactClick(): GtmPayload {
    return popupEvent('GTM_no_plan_covers_contact_click', 'no_plan_covers', 'no_plan_covers_popup', 'app_scan');
}

// ---------- Enterprise: the "Book demo" CTA ----------
// The CTA opens Calendly in a new tab, so the click is the last thing we ever
// learn about this lead — whether they pick a slot happens off our property.
// It is also the entry condition of the enterprise email chain, which is why it
// goes through logEvent (backend row) and not only into the dataLayer.
export function gtmBookDemoClick(entrypoint: string): GtmPayload {
    return {
        event: 'GTM_book_demo_click',
        params: getCommonParams('subscriptions', entrypoint),
    };
}

// ---------- Inline checkout funnel (IncreaseLimitsForm) ----------
// Transport wrapper for the GTM_inline_checkout_* funnel. Most events originate
// inside <InlineCheckout> (the auth package) and arrive here through its `onEvent`
// prop; the post-payment ones (activation / upgrade) are fired from bitfront. Each
// still flows to GTM + the backend via logEvent(). The enumerated registry of event
// names lives in the auth package (`InlineCheckoutEvent`).
export function gtmInlineCheckout(
    event: string,
    params: Record<string, unknown> = {},
    entrypoint: string = 'limits_reached_popup',
    page_type: string = 'app_scan',
): GtmPayload {
    return popupEvent(gtmName(event), 'limits_reached', entrypoint, page_type, params);
}
